(ns ui.wallet.transactions
  "The recent transactions panel of the wallet page: the purchases, refunds
   and credit grants of the current month, read from the monthly statement
   (`:query/subscription-statement`). Usage (daily fees, posts, AI) stays on
   the full statement page, which the panel links to."
  (:require [parts.replicant.query :as query]
            [ui.elements]
            [ui.subscription-statement.page :as statement]
            [ui.wallet.credit-notes :as credit-notes]))

(defn statement-query
  [w]
  (statement/build-query (:wallet/statement-params w)))

(defn load-actions
  "Actions for the page's `:on-load`: remember the current month and query
   its statement."
  []
  (let [params (statement/current-month)]
    [[:store/assoc :wallet/statement-params params]
     [:data/query (statement/build-query params)]]))

(defn money-in?
  "Paddle rows (purchases and refunds) carry no `:feature`, credit grants are
   line-items with a positive total. Usage line-items have a negative total."
  [row]
  (or (not (contains? row :feature))
      (pos? (or (:total row)
                0))))

(defn relabel
  "The stored descriptions talk about trials and subscriptions and are
   English; the wallet shows its own, translated labels.

   Auto Top-Up and \"Add credits\" are both one-time charges (\"Credits
   refill\"). A \"Credits purchase (subscription)\" is the renewal of a
   monthly or yearly plan (Paddle origin `subscription_recurring`) or a
   checkout; labelling it Auto Top-Up made a customer with Auto Top-Up
   switched off ask for a refund of a plan renewal (brain-103)."
  [row]
  (case (:description row)
    "Free trial start credits"
    (assoc row :description [:i18n :transactions/welcome-credits])

    "Credits purchase (subscription)"
    (assoc row :description (if (= "subscription_recurring" (:origin row))
                              [:i18n :transactions/recurring-top-up]
                              [:i18n :transactions/added-credits]))

    "Credits refill"
    (assoc row :description [:i18n :transactions/added-credits])

    "Credits refund"
    (assoc row :description [:i18n :transactions/refund])

    row))

(defn rows
  [result]
  (->> (:month-overview result)
       (filter money-in?)
       (map relabel)))

(def cell-css
  ["whitespace-nowrap" "py-2" "text-sm"])

(defn row
  [{:keys [date description total]}]
  [:tr
   [:td {:class (concat cell-css ["pr-3" "text-gray-500"])}
    (statement/format-date date "yyyy-MM-dd")]
   [:td {:class (concat cell-css ["px-3" "text-gray-900" "w-full"])}
    description]
   [:td {:class (concat cell-css ["pl-3" "text-right" "font-medium"
                                  (if (neg? total)
                                    "text-red-500"
                                    "text-green-600")])}
    (statement/plus-prefix (statement/two-decimals total))]])

(defn table
  [rows*]
  [:table.mt-4.min-w-full.divide-y.divide-gray-300
   [:thead
    [:tr
     (map (fn [[label css]]
            [:th {:class (concat ["py-2" "text-xs" "font-medium" "text-gray-500"]
                                 css)}
             label])
          [[[:i18n :transactions/date] ["pr-3" "text-left"]]
           [[:i18n :transactions/description] ["px-3" "text-left"]]
           [[:i18n :transactions/amount] ["pl-3" "text-right"]]])]]
   [:tbody.divide-y.divide-gray-200
    (map row rows*)]])

(defn links
  []
  (let [css ["text-sm" "text-blue-600" "hover:underline"]]
    [:div.flex.gap-4
     [:ui/a {:ui/location {:location/page-id :pages/subscription-statement
                           :location/params {}}
             :class css}
      [:i18n :transactions/full-statement]]
     [:a {:href "/subscription/invoices"
          :target "_blank"
          :class css}
      [:i18n :transactions/invoices]]
     (credit-notes/link css)]))

(defn body
  [w]
  (let [query* (statement-query w)]
    (cond
      (or (nil? (:wallet/statement-params w))
          (query/loading? w query*))
      [:p.mt-4.text-sm.text-gray-500 [:i18n :page/loading]]

      (query/error? w query*)
      [:p.mt-4.text-sm.text-red-500 [:i18n :transactions/error]]

      :else
      (let [rows* (rows (query/get-result w query*))]
        (if (seq rows*)
          (table rows*)
          [:p.mt-4.text-sm.text-gray-500 [:i18n :transactions/empty]])))))

(defn panel
  [w]
  [:div {:class ui.elements/panel}
   [:div.flex.items-center.justify-between
    [:h2 {:class ui.elements/h3} [:i18n :transactions/title]]
    (links)]
   (body w)])
